import type {
  Ancora,
  Cadeia,
  Celula,
  ColunaSugestao,
  Encaixe,
  ModoJogo,
  PalavraCanonica,
} from '../tipos';
import type { Semente } from '../aleatorio/rng';
import type { IndiceLexico } from '../lexico/indice';
import type { PontosDaJogada } from '../modos/trama';
import { MULTIPLICADOR_MINIMO } from '../modos/trama';
import { CADEIA_VAZIA } from '../tabuleiro/grade';
import type { FilaNovidade } from './novidade';
import { FILA_VAZIA } from './novidade';

/**
 * Estados funcionais da spec §6.
 *
 * ```
 * Carregando ─┬─ sem sessão ──→ Inicio ── 1ª letra ──→ Digitando
 *             └─ restaurada ─────────────────────────→ Digitando
 *
 * Digitando ⇄ PalavraValida ── escolher sugestão ──→ EscolherAncora
 * Digitando  → SemCruzamento (válida, sem sugestão)
 * EscolherAncora ── escolher letra ──→ DraftAncorado ── contém âncora ──→ Digitando
 *
 * PalavraValida ── última jogada do Átrio ──→ Fim          (§22.4)
 * qualquer fase ── tempo esgotado no Nitro ──→ Fim         (§22.2)
 * ```
 *
 * `fim` só existe nos modos com fim. No Bistrô a cadeia não termina, e nenhuma
 * transição leva até lá.
 *
 * O estado é inteiramente serializável: o índice léxico entra como `Ambiente`,
 * nunca como campo. É isso que permite salvar (§12) e desfazer (§11) sem
 * copiar megabytes de dicionário.
 */
export type Fase =
  | 'carregando'
  | 'inicio'
  | 'digitando'
  | 'palavra-valida'
  | 'sem-cruzamento'
  /** R5-15: é palavra do dicionário, mas já está no tabuleiro. */
  | 'repetida'
  | 'escolher-ancora'
  | 'draft-ancorado'
  | 'fim';

/** Origem fixa da primeira palavra-base. */
export const ORIGEM_INICIAL: Celula = { coluna: 0, linha: 0 };

/** Distância, em células, entre a âncora e o draft flutuante de T05. */
export const AFASTAMENTO_PREVIEW = 2;

export interface FalhaDicionario {
  readonly mensagem: string;
  readonly detalhe?: string;
}

/**
 * Snapshot de desfazer (§11). A profundidade é do modo, não do tipo: cada
 * snapshot carrega o anterior, e quem decide onde a corrente para é
 * `historicoDaJogada`. No Bistrô ela não para.
 */
export interface Instantaneo extends Omit<EstadoJogo, 'anterior'> {
  readonly anterior?: Instantaneo;
}

export interface EstadoJogo {
  readonly fase: Fase;
  readonly modo: ModoJogo;
  readonly semente: Semente;
  readonly cadeia: Cadeia;

  /**
   * Pontuação acumulada (§22).
   *
   * Calculada em todo modo, porque é função pura da cadeia e não custa nada
   * manter; é o modo que decide se ela aparece e se a partida acaba. Assim há
   * um caminho de código só, sempre exercitado pelos testes.
   */
  readonly pontos: number;
  /**
   * Multiplicador em vigor — a aposta feita ao escolher a âncora, que a
   * próxima palavra-base cobra ou perde (§22.3).
   */
  readonly multiplicador: number;
  /** Detalhe da última jogada pontuada, para o painel mostrar de onde veio. */
  readonly ultimaJogada?: PontosDaJogada;

  /** Duração escolhida para a corrida, em segundos (§22.3). */
  readonly duracaoSegundos?: number;

  /**
   * Dia da Trama em curso, `AAAA-MM-DD` no fuso do jogo (§23.2).
   *
   * Chega de fora, como o prazo da corrida: o núcleo não lê o relógio
   * (ADR-0001). Ausente nos outros modos.
   */
  readonly dia?: string;
  /** A palavra escondida de hoje (§23.2). */
  readonly resposta?: PalavraCanonica;
  /**
   * Nível em curso do Palavredo, começando em 1 (§22.7).
   *
   * Ausente nos outros modos, como `dia` e `terminaEm`.
   */
  readonly nivel?: number;
  /**
   * Posição na cadeia a partir da qual o nível conta (§22.7).
   *
   * É o `turno` que a próxima palavra receberá quando o nível começa. Guardar
   * este número, e não o progresso, é o que impede o placar de divergir do
   * tabuleiro: tudo o mais é derivado de `(cadeia, nivelDesde, desafio)`.
   */
  readonly nivelDesde?: number;

  /**
   * Instante em que a corrida acaba, em epoch ms.
   *
   * Injetado por quem chama — o núcleo não lê o relógio (ADR-0001). Ausente
   * antes da primeira letra: o cronômetro começa a correr quando o jogador
   * começa a jogar, não quando escolhe o modo (T12-02).
   */
  readonly terminaEm?: number;

  /** Texto aceito no campo, já filtrado (§4.1). Caixa preservada. */
  readonly draft: string;
  /** Célula da primeira letra do draft. */
  readonly origemDraft: Celula;
  /**
   * `true` enquanto o draft ancorado ainda não contém a âncora: ele flutua
   * preso à célula escolhida e não ocupa células permanentes (T05-02).
   */
  readonly draftProvisorio: boolean;

  /** Presente em `escolher-ancora` e nas fases ancoradas seguintes. */
  readonly ancora?: Ancora;
  /**
   * Célula em que a ponte atual cruzou a palavra-base anterior. É a
   * "interseção anterior" de §3: fica marcada com `×` e não pode ser escolhida
   * como âncora (T04-03).
   */
  readonly intersecaoDaPonte?: Celula;
  /** Ocorrências da âncora no draft, da melhor para a pior (T06-04). */
  readonly encaixes: readonly Encaixe[];
  /** Índice, dentro de `encaixes`, da ocorrência ativa. */
  readonly encaixeAtivo: number;

  readonly colunas: readonly ColunaSugestao[];
  readonly fila: FilaNovidade;
  /** Células da base já atravessadas por uma ponte (§5.13). */
  readonly celulasJaCruzadas: readonly Celula[];

  readonly falhaDicionario?: FalhaDicionario;
  /** Anúncio pendente para a região `aria-live` (§13). */
  readonly anuncio?: string;

  readonly anterior?: Instantaneo;
}

/** Dependências não serializáveis do redutor. */
export interface Ambiente {
  readonly indice: IndiceLexico;
}

export function estadoInicial(
  semente: Semente,
  modo: ModoJogo = 'zen',
  duracaoSegundos?: number,
): EstadoJogo {
  return {
    fase: 'carregando',
    modo,
    ...(duracaoSegundos === undefined ? {} : { duracaoSegundos }),
    // §22.7: o arcade abre no nível 1, contando desde o começo da cadeia.
    ...(modo === 'palavredo' ? { nivel: 1, nivelDesde: 0 } : {}),
    pontos: 0,
    multiplicador: MULTIPLICADOR_MINIMO,
    semente,
    cadeia: CADEIA_VAZIA,
    draft: '',
    origemDraft: ORIGEM_INICIAL,
    draftProvisorio: false,
    encaixes: [],
    encaixeAtivo: 0,
    colunas: [],
    fila: FILA_VAZIA,
    celulasJaCruzadas: [],
  };
}

/* ── Seletores ─────────────────────────────────────────────────────────── */

export function estaAncorado(estado: EstadoJogo): boolean {
  return estado.ancora !== undefined;
}

/** Sugestões podem ser escolhidas? (§5.4, §5.5, T02-04) */
export function podeEscolherSugestao(estado: EstadoJogo): boolean {
  return estado.fase === 'palavra-valida';
}

/** `Desfazer` está habilitado? (T01-03, §11) */
export function podeDesfazer(estado: EstadoJogo): boolean {
  // Partida encerrada não se desfaz: o placar já é o resultado (§22.3).
  if (estado.fase === 'fim') return false;
  if (estado.draft.length > 0) return true;
  return estado.anterior !== undefined;
}

/**
 * A Trama está jogável? (§23)
 *
 * Sem a palavra do dia não há o que deduzir — o dicionário ainda não chegou.
 */
export function termoPronto(estado: EstadoJogo): boolean {
  return estado.modo === 'termo' && estado.resposta !== undefined && estado.resposta !== '';
}

/**
 * O arcade está em curso? (§22.7)
 *
 * O nível pode faltar num estado de Palavredo restaurado de uma sessão gravada
 * antes do modo existir; quem lê usa o piso, e não `undefined`.
 */
export function nivelAtual(estado: EstadoJogo): number {
  return estado.modo === 'palavredo' ? (estado.nivel ?? 1) : 1;
}

/** Desde que ponto da cadeia o nível em curso conta (§22.7). */
export function nivelDesdeDe(estado: EstadoJogo): number {
  return estado.nivelDesde ?? 0;
}

/** A partida acabou e o que resta é olhar o tabuleiro (§22.3). */
export function acabou(estado: EstadoJogo): boolean {
  return estado.fase === 'fim';
}

/**
 * A corrida já começou a contar? (§22.2, T12-02)
 *
 * O cronômetro nasce na primeira letra, não na escolha do modo: entre uma
 * coisa e outra há o tempo de a pessoa se ajeitar, e ele não é dela.
 */
export function corridaEmCurso(estado: EstadoJogo): boolean {
  return estado.modo === 'corre' && estado.fase !== 'fim' && estado.terminaEm !== undefined;
}

/** Falta começar a contar: modo Nitro, partida viva e cronômetro parado. */
export function corridaPorComecar(estado: EstadoJogo): boolean {
  return estado.modo === 'corre' && estado.fase !== 'fim' && estado.terminaEm === undefined;
}

/**
 * Fases sem draft aberto.
 *
 * Em `carregando` o jogo ainda não começou, em `escolher-ancora` a vez é de
 * escolher uma letra, e em `fim` a partida acabou. Nenhuma delas aceita texto,
 * e em nenhuma delas há o que recalcular. É uma função só porque as duas
 * perguntas — "isso entra?" e "isso deriva?" — têm de ter sempre a mesma
 * resposta: quando divergiram, texto entrou numa partida encerrada.
 */
export function semDraftAberto(estado: EstadoJogo): boolean {
  return estado.fase === 'carregando' || estado.fase === 'escolher-ancora' || estado.fase === 'fim';
}

export function sugestoesVisiveis(estado: EstadoJogo): readonly ColunaSugestao[] {
  return estado.fase === 'escolher-ancora' ? [] : estado.colunas;
}

export function encaixeAtivoDe(estado: EstadoJogo): Encaixe | undefined {
  return estado.encaixes[estado.encaixeAtivo];
}

/** Snapshot sem histórico: é o que se guarda ao confirmar uma jogada (§11). */
export function instantaneoDe(estado: EstadoJogo): Instantaneo {
  const { anterior: _anterior, ...resto } = estado;
  return resto;
}

/**
 * O histórico que uma jogada confirmada deixa para trás (§11).
 *
 * É aqui, e só aqui, que a profundidade de desfazer é decidida — por modo,
 * porque desfazer custa coisas diferentes em cada um:
 *
 * - **Bistrô:** a corrente inteira. Não há placar nem fim, então nada se ganha
 *   por não poder voltar; o tabuleiro é um rascunho e rascunho se apaga.
 * - **Trama e Palavredo:** nada. Cada palavra confirmada é um palpite gasto —
 *   na Trama contra a palavra do dia, no Palavredo contra o objetivo do nível
 *   — e palpite que se desmancha não custou nada. O que volta atrás nesses
 *   dois é só a letra escolhida *depois* da jogada, que ainda não cobrou nada
 *   de ninguém: a corrente para aqui, e `escolher-ancora` empilha um passo
 *   sobre este.
 * - **Nitro e Átrio:** uma jogada, como sempre foi (§22.5, atrás do portão).
 */
export function historicoDaJogada(estado: EstadoJogo): Instantaneo | undefined {
  if (estado.modo === 'termo' || estado.modo === 'palavredo') return undefined;
  if (estado.modo === 'zen') return estado;
  return instantaneoDe(estado);
}
